import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  AlertTriangle,
  Download,
  Loader2,
  RefreshCw,
  ZoomIn,
} from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { saveAs } from "file-saver";
import UIModal from "@src/components/UIModal/UIModal";
import { useNotification } from "@src/context/NotificationContext";
import storyLogo from "@assets/img/logo-for-stories.svg";
import { ICuratorMedia } from "@apis/curatorApis";
import { ICuratorShowPromo } from "@src/types/show";
import { buildMediaUrl } from "@src/utils/mediaUrl";
import {
  buildStoryFileName,
  getDefaultStoryFields,
  renderInstagramStory,
} from "./instagramStory/renderInstagramStory";
import {
  STORY_FIELD_DEFAULTS,
  STORY_TIME_OPTIONS,
  STORY_WEEKDAY_OPTIONS,
  decodeStoryTimeValue,
  encodeStoryTimeValue,
  resolveStoryTimeValue,
} from "./instagramStory/constants";
import { buildStoryFieldsFromShow } from "./instagramStory/storyFieldsFromShow";
import { canvasToBlob } from "./instagramStory/loadImage";
import type {
  StoryTemplateFields,
  StoryTemplateId,
} from "./instagramStory/types";
import { DownloadButton, StoryButton } from "./SocialMediaPromo.styles";

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${({ theme }) => theme.margins.large};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.margins.medium};
`;

const PreviewFrame = styled.div<{ $clickable?: boolean }>`
  width: 270px;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.gray400};
  background: ${({ theme }) => theme.colors.darkerBg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? "zoom-in" : "default")};
`;

const PreviewImageButton = styled.button`
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  position: relative;
`;

const PreviewZoomHint = styled.span`
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewError = styled.p`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.red};
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
`;

const PreviewErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background: ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TemplateToggleRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.margins.small};
  width: 100%;
`;

const TemplateToggle = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.gray400};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.gray300};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.black : theme.colors.white};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.small};
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.gray150};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const FieldSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background: ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, currentColor 50%),
    linear-gradient(135deg, currentColor 50%, transparent 50%);
  background-position:
    calc(100% - 18px) calc(50% + 2px),
    calc(100% - 12px) calc(50% + 2px);
  background-size:
    6px 6px,
    6px 6px;
  background-repeat: no-repeat;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FieldInput = styled.input<{ $warning?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $warning }) =>
      $warning ? theme.colors.primary : theme.colors.gray400};
  background: ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// Sits inside a FieldGroup label, which is uppercase and letter-spaced for
// field names — this is a sentence, so it opts back out of both.
const FieldWarning = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
  text-transform: none;
  letter-spacing: 0;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const ModalActionButton = styled(DownloadButton)`
  min-width: 180px;
`;

type InstagramStoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  media: ICuratorMedia | null;
  curatorName: string;
  /** The curator's shows, used to prefill the template. Ordered by the page so
   *  the show airing soonest comes first. */
  shows?: ICuratorShowPromo[];
};

// Module-level so the `shows` default keeps a stable identity: a fresh `[]` on
// every render would re-run the prefill effect and wipe what the user typed.
const NO_SHOWS: ICuratorShowPromo[] = [];

// Show title doubles as the show picker. This sentinel is the escape hatch:
// pick it and the field turns back into the free-text input, for a promo that
// isn't one of the curator's scheduled shows.
const CUSTOM_SHOW_TITLE = "__custom__";

const TEMPLATE_LABELS: Record<StoryTemplateId, string> = {
  v1: "Full bleed",
  v2: "Framed",
};

const getHostPrefix = (_templateId: StoryTemplateId): string =>
  STORY_FIELD_DEFAULTS.hostPrefix;

const InstagramStoryModal: React.FC<InstagramStoryModalProps> = ({
  isOpen,
  onClose,
  media,
  curatorName,
  shows = NO_SHOWS,
}) => {
  const { addNotification } = useNotification();
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  const [templateId, setTemplateId] = useState<StoryTemplateId>("v1");
  const [selectedShowId, setSelectedShowId] = useState<string>("");
  const [fields, setFields] = useState<StoryTemplateFields>(() =>
    getDefaultStoryFields(curatorName)
  );
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewError, setPreviewError] = useState<string>("");
  const [isRendering, setIsRendering] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [previewLightboxOpen, setPreviewLightboxOpen] = useState(false);
  const [hostPrefixWarned, setHostPrefixWarned] = useState(false);
  // Set by every edit, so a late prefill knows to stay out of the way. A ref
  // rather than state: it must not itself re-run the prefill effect.
  const formTouchedRef = useRef(false);
  const previewUrlRef = useRef("");

  useLayoutEffect(() => {
    if (!isOpen) return;

    setPreviewError("");
    setPreviewUrl("");
    setIsRendering(Boolean(media?.url));
  }, [isOpen, media?.id, media?.url]);

  // Opening the modal on a new story starts from a clean slate.
  useEffect(() => {
    if (!isOpen) return;

    formTouchedRef.current = false;
    setTemplateId("v1");
    setRenderAttempt(0);
    setPreviewLightboxOpen(false);
    setHostPrefixWarned(false);
  }, [isOpen, media?.id]);

  // Prefill from the curator's own show data rather than the placeholders, so
  // nobody retypes what the dashboard already knows.
  //
  // The shows resolve after the gallery does — `fetchCuratorShowPromos` makes
  // two sequential requests — so the modal can open on placeholders and be
  // filled a moment later. Skip that once the form has been touched: landing on
  // top of what someone is typing loses their work.
  useEffect(() => {
    if (!isOpen || formTouchedRef.current) return;

    const show = shows[0];
    setSelectedShowId(show?.documentId ?? CUSTOM_SHOW_TITLE);
    setFields(buildStoryFieldsFromShow(show, curatorName));
  }, [isOpen, curatorName, media?.id, shows]);

  const imageUrl = useMemo(() => {
    if (!media?.url) return "";
    return buildMediaUrl(media.url);
  }, [media?.url]);

  const renderKey = useMemo(
    () =>
      JSON.stringify({
        imageUrl,
        templateId,
        fields,
      }),
    [fields, imageUrl, templateId]
  );

  useEffect(() => {
    if (!isOpen || !imageUrl) {
      setPreviewUrl("");
      setPreviewError("");
      setIsRendering(false);
      return;
    }

    setIsRendering(true);
    setPreviewError("");

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const canvas = await renderInstagramStory({
          templateId,
          imageUrl,
          fields,
          logoSrc: storyLogo,
        });
        if (cancelled) return;

        const blob = await canvasToBlob(canvas);
        const nextUrl = URL.createObjectURL(blob);

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }

        previewUrlRef.current = nextUrl;
        setPreviewUrl(nextUrl);
      } catch (error) {
        if (cancelled) return;

        setPreviewUrl("");
        setPreviewError(
          error instanceof Error
            ? error.message
            : "Failed to render story preview"
        );
      } finally {
        if (!cancelled) {
          setIsRendering(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [imageUrl, isOpen, renderAttempt, renderKey, templateId]);

  const handleRetryPreview = (): void => {
    setPreviewError("");
    setIsRendering(true);
    setRenderAttempt((attempt) => attempt + 1);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = "";
      }
    };
  }, []);

  const handleTemplateChange = (nextTemplateId: StoryTemplateId): void => {
    setTemplateId(nextTemplateId);
    setFields((prev) => ({
      ...prev,
      hostPrefix: getHostPrefix(nextTemplateId),
    }));
  };

  const updateField = (key: keyof StoryTemplateFields, value: string): void => {
    formTouchedRef.current = true;
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  // Picking a show refills the fields that show owns — its title and the day
  // and time it airs. Anything typed into the host fields by hand is left
  // alone, and "Custom title" keeps the title already in the field so it can be
  // edited rather than retyped.
  const handleShowChange = (documentId: string): void => {
    formTouchedRef.current = true;
    setSelectedShowId(documentId);

    const show = shows.find((item) => item.documentId === documentId);
    if (!show) return;

    const showFields = buildStoryFieldsFromShow(show, curatorName);
    setFields((prev) => ({
      ...prev,
      dayLabel: showFields.dayLabel,
      timeHour: showFields.timeHour,
      timePeriod: showFields.timePeriod,
      showTitle: showFields.showTitle,
    }));
  };

  const handleTimeChange = (value: string): void => {
    formTouchedRef.current = true;
    const { hour, period } = decodeStoryTimeValue(value);
    setFields((prev) => ({
      ...prev,
      timeHour: hour,
      timePeriod: period,
    }));
  };

  const selectedTimeValue = resolveStoryTimeValue(
    fields.timeHour,
    fields.timePeriod
  );

  // Day, time and title now arrive from the show, which leaves the host prefix
  // as the one field still on its placeholder — and the story renders it
  // verbatim, so the only way to catch it is to look at a finished PNG.
  const isHostPrefixPlaceholder =
    fields.hostPrefix.trim().toUpperCase() === STORY_FIELD_DEFAULTS.hostPrefix;
  const showHostPrefixWarning = isHostPrefixPlaceholder && hostPrefixWarned;

  const handleDownload = async (): Promise<void> => {
    if (!imageUrl || !media) return;

    // Warn once, then get out of the way: pressing Download again goes through,
    // because "HOST PREFIX" is occasionally what someone actually wants.
    if (isHostPrefixPlaceholder && !hostPrefixWarned) {
      setHostPrefixWarned(true);
      return;
    }

    try {
      setIsDownloading(true);
      const canvas = await renderInstagramStory({
        templateId,
        imageUrl,
        fields,
        logoSrc: storyLogo,
      });
      const blob = await canvasToBlob(canvas);
      saveAs(blob, buildStoryFileName(templateId, curatorName, media.name));
      addNotificationRef.current("Instagram story downloaded", "success");
    } catch {
      addNotificationRef.current("Failed to download story image", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!media) return null;

  const previewLightbox = (
    <Lightbox
      open={previewLightboxOpen}
      close={() => setPreviewLightboxOpen(false)}
      slides={previewUrl ? [{ src: previewUrl, alt: "Story preview" }] : []}
      styles={{ root: { "--yarl__portal_zindex": 10001 } }}
    />
  );

  return (
    <>
      <UIModal
        isOpen={isOpen}
        onClose={onClose}
        title="Create Instagram Story"
        modalStyles={{
          width: "min(920px, 95vw)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        actions={
          <ModalActionButton
            disabled={isRendering || isDownloading || !previewUrl}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <>
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Downloading...
              </>
            ) : (
              <>
                <Download size={16} />
                {showHostPrefixWarning ? "Download anyway" : "Download Story"}
              </>
            )}
          </ModalActionButton>
        }
      >
        <ModalBody>
          <PreviewColumn>
            <PreviewFrame $clickable={Boolean(previewUrl)}>
              {isRendering ? (
                <Loader2
                  size={32}
                  style={{
                    color: "#FFF057",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : previewUrl ? (
                <PreviewImageButton
                  type="button"
                  aria-label="Open story preview"
                  onClick={() => setPreviewLightboxOpen(true)}
                >
                  <PreviewImage src={previewUrl} alt="Story preview" />
                  <PreviewZoomHint>
                    <ZoomIn size={12} />
                    View
                  </PreviewZoomHint>
                </PreviewImageButton>
              ) : previewError ? (
                <PreviewErrorState>
                  <PreviewError>{previewError}</PreviewError>
                  <RetryButton
                    type="button"
                    onClick={handleRetryPreview}
                    disabled={isRendering}
                  >
                    <RefreshCw size={14} />
                    Retry preview
                  </RetryButton>
                </PreviewErrorState>
              ) : (
                <PreviewError>Preview unavailable</PreviewError>
              )}
            </PreviewFrame>

            <TemplateToggleRow>
              {(Object.keys(TEMPLATE_LABELS) as StoryTemplateId[]).map((id) => (
                <TemplateToggle
                  key={id}
                  type="button"
                  $active={templateId === id}
                  onClick={() => handleTemplateChange(id)}
                >
                  {TEMPLATE_LABELS[id]}
                </TemplateToggle>
              ))}
            </TemplateToggleRow>
          </PreviewColumn>

          <FieldsColumn>
            <FieldGroup>
              Day
              <FieldSelect
                value={fields.dayLabel}
                onChange={(e) => updateField("dayLabel", e.target.value)}
              >
                {STORY_WEEKDAY_OPTIONS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </FieldSelect>
            </FieldGroup>

            <FieldGroup>
              Time
              <FieldSelect
                value={selectedTimeValue}
                onChange={(e) => handleTimeChange(e.target.value)}
              >
                {STORY_TIME_OPTIONS.map((option) => (
                  <option
                    key={encodeStoryTimeValue(option.hour, option.period)}
                    value={encodeStoryTimeValue(option.hour, option.period)}
                  >
                    {option.label}
                  </option>
                ))}
              </FieldSelect>
            </FieldGroup>

            <FieldGroup>
              Show title
              {/* With shows to choose from the field is a picker, so the common
                  case is one click; the text input is still there for a promo
                  that isn't one of them. A curator with no shows only ever sees
                  the input. */}
              {shows.length > 0 && (
                <FieldSelect
                  value={selectedShowId}
                  onChange={(e) => handleShowChange(e.target.value)}
                >
                  {shows.map((show) => (
                    <option key={show.documentId} value={show.documentId}>
                      {show.title || "Untitled show"}
                    </option>
                  ))}
                  <option value={CUSTOM_SHOW_TITLE}>Custom title…</option>
                </FieldSelect>
              )}
              {(!shows.length || selectedShowId === CUSTOM_SHOW_TITLE) && (
                <FieldInput
                  value={fields.showTitle}
                  onChange={(e) => updateField("showTitle", e.target.value)}
                  placeholder={STORY_FIELD_DEFAULTS.showTitle}
                />
              )}
            </FieldGroup>

            <FieldGroup>
              Host prefix
              <FieldInput
                value={fields.hostPrefix}
                onChange={(e) => updateField("hostPrefix", e.target.value)}
                placeholder={STORY_FIELD_DEFAULTS.hostPrefix}
                $warning={showHostPrefixWarning}
              />
              {showHostPrefixWarning && (
                <FieldWarning role="alert">
                  <AlertTriangle size={13} />
                  Still the placeholder — the story will read &ldquo;
                  {STORY_FIELD_DEFAULTS.hostPrefix}&rdquo;. Edit it, or press
                  Download again to keep it.
                </FieldWarning>
              )}
            </FieldGroup>

            <FieldGroup>
              Host name
              <FieldInput
                value={fields.hostName}
                onChange={(e) => updateField("hostName", e.target.value)}
                placeholder={curatorName || STORY_FIELD_DEFAULTS.hostName}
              />
            </FieldGroup>
          </FieldsColumn>
        </ModalBody>
      </UIModal>
      {previewLightbox}
    </>
  );
};

type StoryCardActionsProps = {
  onOpenStory: () => void;
  onDownload: () => void;
  isDownloading: boolean;
};

export const StoryCardActions: React.FC<StoryCardActionsProps> = ({
  onOpenStory,
  onDownload,
  isDownloading,
}) => (
  <>
    <StoryButton type="button" onClick={onOpenStory}>
      Create Story
    </StoryButton>
    <DownloadButton disabled={isDownloading} onClick={onDownload}>
      {isDownloading ? (
        <>
          <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
          Downloading...
        </>
      ) : (
        <>
          <Download size={16} />
          Download Image
        </>
      )}
    </DownloadButton>
  </>
);

export default InstagramStoryModal;
